import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Sparkles, Crown, Check, Loader2, CreditCard, Leaf, MessageCircle, Map, BookOpen, Target, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PremiumPage() {
  const { i18n } = useTranslation();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isPremiumEmergence, setIsPremiumEmergence] = useState(false);

  const isFrench = i18n.language === 'fr';
  const price = 19.99;

  useEffect(() => {
    checkPremiumStatus();
  }, []);

  const checkPremiumStatus = async () => {
    const email = localStorage.getItem("emergence_email") || user?.email;
    if (email) {
      try {
        const response = await axios.get(`${API}/emergence/premium/status?email=${encodeURIComponent(email)}`);
        setIsPremiumEmergence(response.data.is_premium);
      } catch (error) {
        console.error("Error checking premium:", error);
      }
    }
  };

  const handleUpgrade = async () => {
    // Si connecté, utiliser le système Épanoui
    if (user && token) {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/payments/checkout`, {
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = response.data.checkout_url;
      } catch (error) {
        console.error("Checkout error:", error);
        toast.error(error.response?.data?.detail || "Erreur lors du paiement");
        setLoading(false);
      }
    } else {
      // Sinon, demander l'email et utiliser le système Émergence
      const email = localStorage.getItem("emergence_email");
      if (!email) {
        toast.error(isFrench ? "Veuillez d'abord faire un diagnostic" : "Please do a diagnostic first");
        return;
      }
      
      setLoading(true);
      try {
        const response = await axios.post(`${API}/emergence/premium/checkout?email=${encodeURIComponent(email)}&language=${i18n.language}`);
        window.location.href = response.data.checkout_url;
      } catch (error) {
        toast.error(isFrench ? "Erreur lors du paiement" : "Payment error");
        setLoading(false);
      }
    }
  };

  const isPremium = user?.is_premium || isPremiumEmergence;

  const features = [
    {
      icon: <Target className="w-5 h-5" />,
      title: isFrench ? "Diagnostics illimités" : "Unlimited diagnostics",
      description: isFrench ? "Blocages + Changement" : "Blocks + Change"
    },
    {
      icon: <Map className="w-5 h-5" />,
      title: isFrench ? "3 Parcours guidés 21 jours" : "3 Guided 21-day journeys",
      description: isFrench ? "Sens, Épuisement, Changement" : "Meaning, Exhaustion, Change"
    },
    {
      icon: <MessageCircle className="w-5 h-5" />,
      title: isFrench ? "Coach IA illimité" : "Unlimited AI coach",
      description: isFrench ? "Chat bienveillant 24/7" : "Caring chat 24/7"
    },
    {
      icon: <BookOpen className="w-5 h-5" />,
      title: isFrench ? "Journal intelligent complet" : "Full smart journal",
      description: isFrench ? "Réflexions IA incluses" : "AI reflections included"
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      title: isFrench ? "Analyses IA personnalisées" : "Personalized AI analyses",
      description: isFrench ? "GPT-5.2 avancé" : "Advanced GPT-5.2"
    },
    {
      icon: <Heart className="w-5 h-5" />,
      title: isFrench ? "Export PDF + Email" : "PDF export + Email",
      description: isFrench ? "Partagez vos résultats" : "Share your results"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF9F6] to-[#F0F4F3]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#2F4F4F] hover:opacity-80" data-testid="back-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{isFrench ? 'Accueil' : 'Home'}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2F4F4F]" />
            <span className="font-heading text-lg text-[#2F4F4F]">Épanoui</span>
          </div>
          <div className="w-20" />
        </div>
      </header>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#E2725B]/10 to-[#D4A574]/10 text-[#E2725B] text-sm font-medium mb-6">
              <Crown className="w-4 h-4" />
              {isFrench ? 'Accès complet' : 'Full access'}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#2D2D2D] mb-4">
              {isFrench ? 'Tout Épanoui en illimité' : 'Unlimited Épanoui'}
            </h1>
            <p className="text-lg text-[#5C5C5C] max-w-2xl mx-auto">
              {isFrench 
                ? "Abonnement mensuel avec accès à toutes les fonctionnalités : diagnostics, parcours 21 jours, coach IA, journal intelligent."
                : "One payment, lifetime access to all features: diagnostics, 21-day journeys, AI coach, smart journal."}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Features */}
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-semibold text-[#2D2D2D] mb-6">
                {isFrench ? "Tout ce qui est inclus" : "Everything included"}
              </h2>
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2F4F4F]/10 to-[#3B7A8C]/10 flex items-center justify-center text-[#2F4F4F] flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2D2D2D]">{feature.title}</h3>
                    <p className="text-sm text-[#8A8A8A]">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing Card */}
            <div className="lg:sticky lg:top-28">
              <Card className="bg-white border-0 shadow-2xl overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-[#2F4F4F] via-[#3B7A8C] to-[#D4A574]" />
                <CardHeader className="text-center pb-4">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#E2725B] to-[#D4A574] text-white flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-10 h-10" />
                  </div>
                  <CardTitle className="font-heading text-2xl text-[#2D2D2D]">
                    Premium
                  </CardTitle>
                  <p className="text-sm text-[#8A8A8A]">
                    {isFrench ? "Accès à vie" : "Lifetime access"}
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-center py-6 border-y border-gray-100">
                    <span className="text-5xl font-bold text-[#2D2D2D]">{price}€</span>
                    <p className="text-[#8A8A8A] mt-1">{isFrench ? "par mois" : "per month"}</p>
                  </div>

                  <div className="py-6">
                    {isPremium ? (
                      <div className="text-center py-6 bg-gradient-to-br from-[#A8C3A0]/20 to-[#8FB8A8]/20 rounded-xl">
                        <Crown className="w-8 h-8 text-[#2F4F4F] mx-auto mb-2" />
                        <p className="font-semibold text-[#2F4F4F]">
                          {isFrench ? 'Tu es Premium ! 🎉' : 'You are Premium! 🎉'}
                        </p>
                        <p className="text-sm text-[#5C5C5C] mt-1">
                          {isFrench ? "Profite de toutes les fonctionnalités" : "Enjoy all features"}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Button
                          onClick={handleUpgrade}
                          disabled={loading}
                          className="w-full h-14 rounded-full bg-gradient-to-r from-[#E2725B] to-[#D4A574] hover:opacity-90 text-white text-lg shadow-lg"
                          data-testid="upgrade-btn"
                        >
                          {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5 mr-2" />
                              {isFrench ? 'Devenir Premium' : 'Go Premium'}
                            </>
                          )}
                        </Button>
                        
                        {!user && !localStorage.getItem("emergence_email") && (
                          <p className="text-center text-sm text-[#8A8A8A] mt-4">
                            {isFrench ? "Fais d'abord un diagnostic gratuit" : "Do a free diagnostic first"}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-6 text-xs text-[#8A8A8A] pt-4 border-t border-gray-100">
                    <span className="flex items-center gap-1">🔒 {isFrench ? 'Sécurisé' : 'Secure'}</span>
                    <span className="flex items-center gap-1">💳 Stripe</span>
                    <span className="flex items-center gap-1">✨ {isFrench ? 'À vie' : 'Lifetime'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Parcours preview */}
              <div className="mt-6 p-4 bg-white/50 rounded-xl border border-[#3B7A8C]/10">
                <p className="text-sm font-medium text-[#2D5A66] mb-3">
                  {isFrench ? "Parcours inclus :" : "Included journeys:"}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 rounded-full bg-white text-xs text-[#3D4F5F]">🧭 {isFrench ? "Retrouver du sens" : "Finding meaning"}</span>
                  <span className="px-3 py-1.5 rounded-full bg-white text-xs text-[#3D4F5F]">🔥 {isFrench ? "Sortir de l'épuisement" : "Overcoming exhaustion"}</span>
                  <span className="px-3 py-1.5 rounded-full bg-white text-xs text-[#3D4F5F]">🦋 {isFrench ? "Oser changer" : "Daring to change"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
